import apiClient from './api';
import type { User, PaginatedResponse, PaginationParams } from '@/types';

export const userService = {
  /**
   * Get paginated list of users
   */
  async getUsers(params?: PaginationParams): Promise<PaginatedResponse<User>> {
    const { data } = await apiClient.get<PaginatedResponse<User>>('/users', { params });
    return data;
  },

  /**
   * Get single user by ID
   */
  async getUser(id: string): Promise<User> {
    const { data } = await apiClient.get<User>(`/users/${id}`);
    return data;
  },

  /**
   * Create new user
   */
  async createUser(userData: Partial<User>): Promise<User> {
    const { data } = await apiClient.post<User>('/users', userData);
    return data;
  },

  /**
   * Update user
   */
  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    const { data } = await apiClient.put<User>(`/users/${id}`, userData);
    return data;
  },

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },

  /**
   * Bulk delete users
   */
  async bulkDeleteUsers(ids: string[]): Promise<void> {
    await apiClient.post('/users/bulk-delete', { ids });
  },

  /**
   * Assign role to user
   */
  async assignRole(userId: string, roleId: string): Promise<void> {
    await apiClient.post(`/users/${userId}/roles`, { role_id: roleId });
  },

  /**
   * Remove role from user
   */
  async removeRole(userId: string, roleId: string): Promise<void> {
    await apiClient.delete(`/users/${userId}/roles/${roleId}`);
  },

  /**
   * Toggle user status (active/inactive)
   */
  async toggleUserStatus(id: string): Promise<User> {
    const { data } = await apiClient.patch<User>(`/users/${id}/toggle-status`);
    return data;
  },

  /**
   * Resend email verification
   */
  async resendVerification(id: string): Promise<{ message: string }> {
    const { data } = await apiClient.post(`/users/${id}/resend-verification`);
    return data;
  },
};
